import { Injector } from '@angular/core';
import { ePlatforms } from '@common/networks';
import { TokenApi } from '@services/api/token';
import { ethers, Signer, TransactionLike } from 'ethers';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

import { IToken } from '../api/token';
import { IStorageHistory, TX_STATE } from '../api/transaction-history';
import { CoinsService } from '../coins/coins.service';
import { MarketsFactoryService } from '../markets/markets-factory.service';
import { StorageService } from '../storage/storage.service';
import { sleep } from '../utils';
import { Currency } from './base-currency';
import { EVMBlockChainExplorerFactoryService } from './explorers/evm-blockchain-explorer.service';
import { ITransactionResponceEx } from './explorers/types';
import { ParseFactory } from './parser-tx/parse-factory.service';
import { TransactionDirection, TransactionStatus } from './types';
import { Wallet } from './wallet.service';

const mockCurrencies = {
    WEENUS: {
        name: 'Basic Attention Token',
        symbol: 'BAT',
        prevent_market: true,
    },
    IOT: {
        name: 'Astra IOT',
        symbol: 'IOT',
        prevent_market: true,
    },
    jc3: {
        name: 'JCoin3',
        symbol: 'JC3',
        prevent_market: true,
    },
    jc6: {
        name: 'JCoin6',
        symbol: 'JC6',
        prevent_market: true,
    },
    prevent_market: [
        '0x2b46b3a32984520f47a194a52863a77f29ca83e1',
        '0xaff4481d10270f50f203e0763e2597776068cbc5',
        '0x935099adf26ea8f7e84a175c930d814f05b2d637',
    ],
};

const marketData = {
    JASMY: {
        market: 'mxc',
        pair: 'JASMY_USDT',
        currency: 'usd',
    },
};

export function EVMCurrenciesFactory(
    symbol: string,
    injector: Injector,
    wallet: Wallet,
    address: string,
    dbToken: IToken,
    tokenAddress?: string,
    id?: number
): Currency
{
    return new EvmCurrency(injector, wallet, address, symbol, dbToken, id);
}

export class EvmCurrency extends Currency
{
    protected signer: Signer;
    protected isLoadNewHistory = false;
    protected feeRequired: any = null;
    protected contract: ethers.Contract;
    protected readonly explorer: any;
    protected marketService: MarketsFactoryService;
    protected explorerService: EVMBlockChainExplorerFactoryService;
    protected baseCoin: IToken;
    protected scanLink: string;

    constructor(
        protected injector: Injector,
        _wallet: Wallet,
        _address: string,
        _symbol: string,
        _dbToken: IToken,
        _id?: number
    )
    {
        super(injector, _wallet, _address, _symbol, _dbToken, _id);
        this.isToken = !!_dbToken.address;
        if (this.isToken)
        {
            this.tokenAddress = _dbToken.address;
        }
        this.explorerService = this.injector.get(
            EVMBlockChainExplorerFactoryService
        );
        this.marketService = this.injector.get(MarketsFactoryService);
        this.scanLink = environment[ this.chainId ].scan;

        this.platform = ePlatforms.EVM;
        this.baseSymbol = _symbol;
        this.parserService = ParseFactory.CreateParse(
            this.platform,
            this.injector
        );

        this.init();
    }

    protected async init(): Promise<any>
    {
        this.provider = this.providerFactoryService.getProvider(this.chainId);
        this.signer = new ethers.VoidSigner(this.address, this.provider as any);

        if (this.isToken)
        {
            this.baseCoin = await this.networksService.getCoinByChainId(
                this.chainId
            );
            this.baseSymbol = this.baseCoin.symbol;
            await this.generateContract();
        }
        this.setupStateSubject.next(true);
    }

    public async isFeeRequired(): Promise<boolean>
    {
        if (this.feeRequired === null)
        {
            this.feeRequired = !!(await this.provider.getFeeData())
                .maxFeePerGas;
        }

        return this.feeRequired;
    }

    public getLinkToExplorer(_txHash: string): string
    {
        return `${ this.scanLink }tx/${ _txHash }`;
    }

    public async getBlockInfo(_blockNumber: number): Promise<ethers.Block>
    {
        return await this.provider.getBlock(_blockNumber);
    }

    public isCoinByChainId(_chainId: number): boolean
    {
        if (!this.isToken && this.chainId === _chainId)
        {
            return true;
        }
        return false;
    }

    public async populateTransaction(
        _transaction: TransactionLike,
        _transfer?: boolean,
        _gassless?: boolean
    ): Promise<any>
    {
        delete _transaction.gasPrice;
        delete _transaction.maxFeePerGas;
        delete _transaction.maxPriorityFeePerGas;

        if (_transaction[ 'gas' ])
        {
            _transaction.gasLimit = BigInt(_transaction[ 'gas' ]);
            delete _transaction[ 'gas' ];
        }

        if (this.isToken)
        {
            return await this.populateContractTransaction(
                _transaction,
                _transfer,
                _gassless
            );
        }

        if (_gassless)
        {
            return {
                ..._transaction,
                from: this.wallet.address,
            };
        }

        const feeRequired = await this.isFeeRequired();
        if (!feeRequired)
        {
            return this.populateTransactionOld(_transaction);
        }

        if (!_transaction.gasLimit)
        {
            const gas = await this.signer.estimateGas(_transaction);
            _transaction.gasLimit = gas;
        }

        const tx = await this.signer.populateTransaction({ ..._transaction });

        return tx;
    }

    public async populateTransactionOld(
        _transaction: TransactionLike
    ): Promise<any>
    {
        delete _transaction.maxPriorityFeePerGas;

        const gas = await this.signer.estimateGas(_transaction);

        const tx = await this.signer.populateTransaction({
            ..._transaction,
            gasLimit: gas,
        });

        return tx;
    }

    public async getFeeLimit(
        _data: { to: string; value: string; },
        _type = 'string'
    ): Promise<string | any>
    {
        if (this.isToken)
        {
            return await this.getFeeLimitContract(_data, _type);
        }
        const txData = {
            ..._data,
            from: this.address,
            value: ethers.parseUnits(_data.value),
        };
        const gas = await this.signer.estimateGas(txData);

        txData[ 'gasLimit' ] = gas + gas / BigInt('15');

        const tx = await this.signer.populateTransaction(txData);

        if (_type === 'string')
        {
            return ethers.formatUnits(tx.gasLimit, 'wei');
        }
        if (_type === 'BigNumber')
        {
            return tx.gasLimit;
        }

        return tx.gasLimit;
    }

    public async estimateFeePrice(
        to: string,
        _value: string,
        _transfer: boolean = true
    ): Promise<string>
    {
        const checkedTx = await this.checkTransaction(to, _value);
        const populatedTx = await this.populateTransaction(
            checkedTx,
            _transfer
        );

        return ethers.formatUnits(
            populatedTx.gasPrice || populatedTx.maxFeePerGas,
            'gwei'
        );
    }

    public async sendTransaction(
        _tx: ethers.TransactionRequest,
        _txInfo: { symbol: string; value: string; to?: string; }
    ): Promise<any>
    {
        console.log(this, JSON.stringify(_tx));
        const coinsService = await this.injector.get(CoinsService);
        let txResponse;
        const feeRequired = await this.isFeeRequired();
        if (!feeRequired)
        {
            delete _tx.maxFeePerGas;
            delete _tx.maxPriorityFeePerGas;
            delete _tx.type;

            txResponse = await this.sendTransactionOld(_tx, _txInfo);

            if (txResponse)
            {
                await this.checkCustomTokens(_tx);
            }

            return txResponse;
        }
        try
        {
            const txInfo = {
                ..._txInfo,
                symbol: _txInfo.symbol || this.symbol,
                icon:
                    this.getTokenIcon() ||
                    (await coinsService.getSymbolIcon(this)),
            };

            delete _tx.gasPrice;

            // delete _tx.maxFeePerGas;

            if (this.isLoadNewHistory)
                this.isLoadNewHistory = !this.isLoadNewHistory;

            txResponse = await this.wallet.sendTransaction(
                _tx,
                txInfo,
                this.chainId
            );

            if (txResponse)
            {
                await this.checkCustomTokens(_tx);
            }

            return txResponse;
        } catch (error)
        {
            console.error(error);
            throw error;
        }
    }

    private async checkCustomTokens(_tx)
    {
        try
        {
            const storage = this.injector.get(StorageService);
            const tokenApi = this.injector.get(TokenApi);
            console.log(this.getBlockchain());
            const [ customTokens, allTokens ] = await Promise.all([
                storage.get('customTokens'),
                tokenApi.get(),
            ]);
            const customToken = customTokens.find(
                (token) =>
                    _tx.chainId === this.chainId &&
                    (token.address.toLowerCase() === _tx.to.toLowerCase() ||
                        token.address.toLowerCase() === _tx.from.toLowerCase())
            );
            if (!customToken)
            {
                return;
            }
            const tokenCurr = allTokens.find(
                (token) =>
                    token.address.toLowerCase() ===
                    customToken?.address.toLowerCase() &&
                    token.blockchainMain.chain_id === customToken?.chainId
            );
            if (tokenCurr)
            {
                await Promise.all([
                    this.wallet.addCurrencyByContract(
                        tokenCurr.address,
                        tokenCurr.symbol,
                        tokenCurr,
                        tokenCurr.id
                    ),
                    tokenApi.setWalletToken(this.wallet.getId(), tokenCurr.id),
                ]);
            }
        } catch (e) { }
    }

    public async sendGasLessTx(
        _tx: ethers.TransactionRequest,
        _txInfo: {
            symbol: string;
            value: string;
            paymaster: string;
            to?: string;
        }
    ): Promise<any>
    {
        const feeRequired = await this.isFeeRequired();
        if (!feeRequired)
        {
            return this.wallet.sendGasLessTx(_tx, _txInfo, this.chainId, true);
        }

        try
        {
            const coinsService = await this.injector.get(CoinsService);
            const txInfo = {
                ..._txInfo,
                symbol: _txInfo.symbol || this.symbol,
                icon:
                    this.getTokenIcon() ||
                    (await coinsService.getSymbolIcon(this)),
            };

            delete _tx.gasPrice;

            if (this.isLoadNewHistory)
                this.isLoadNewHistory = !this.isLoadNewHistory;

            return await this.wallet.sendGasLessTx(_tx, txInfo, this.chainId);
        } catch (error)
        {
            console.error(error);
            throw error;
        }
    }

    public async sendTransactionOld(
        _txData: ethers.TransactionRequest,
        _txInfo: { symbol: string; value: string; to?: string; }
    ): Promise<any>
    {
        try
        {
            const coinsService = await this.injector.get(CoinsService);
            const txInfo = {
                ..._txInfo,
                symbol: _txInfo.symbol || this.symbol,
                icon:
                    this.getTokenIcon() ||
                    (await coinsService.getSymbolIcon(this)),
            };

            delete _txData.maxFeePerGas;
            delete _txData.maxPriorityFeePerGas;
            delete _txData.type;

            if (this.isLoadNewHistory)
                this.isLoadNewHistory = !this.isLoadNewHistory;

            return await this.wallet.sendTransactionOld(
                _txData,
                txInfo,
                this.chainId
            );
        } catch (error)
        {
            console.error(error);
            throw error;
        }
    }

    public getFeeUnit(): string
    {
        return 'gwei';
    }

    public async getPrice(
        _currency: string = 'usd',
        _getBaseCoinPrise: boolean = false
    ): Promise<{ price: string; price_change: string; }>
    {
        // return {price: '0.123', price_change:'5'}
        if (!_getBaseCoinPrise && this.isToken)
        {
            return await this.getPriceContract(_currency);
        }
        return await this.pricesService.getPrice(
            this.baseSymbol,
            _currency,
            this.chainId
        );
    }

    get onSetup(): Observable<boolean>
    {
        return this.setupStateSubject.asObservable();
    }

    public async checkTransaction(_to: string, _value: string): Promise<any>
    {
        if (this.isToken)
        {
            return await this.checkTransactionContract(_to, _value);
        }

        const txRequest = this.signer.populateCall({
            to: _to,
            value: ethers.parseEther(_value.toString()),
        });

        return txRequest;
    }

    public async getBalance(): Promise<any>
    {
        try
        {
            if (this.isToken)
            {
                return await this.getBalanceContract();
            }
            return await this.getBaseCurrencyBalance();
        } catch (e)
        {
            console.log(e);
        }

        return '0';
    }

    public async getBaseCurrencyBalance(): Promise<any>
    {
        const balance = await this.signer.provider.getBalance(
            await this.signer.getAddress()
        );

        return ethers.formatEther(balance);
    }

    public prepareFee(
        _price: string,
        _limit: string
    ): { price: any; limit: any; }
    {
        return {
            price: ethers.parseUnits(_price.split('.')[ 0 ], 'gwei'),
            limit: ethers.parseUnits(_limit.split('.')[ 0 ], 'wei'),
        };
    }

    public async getTransactionByHash(
        _hash: string,
        _parseData: boolean = true,
        _notCallContract: boolean = false
    ): Promise<ITransactionResponceEx>
    {
        if (!_notCallContract && this.isToken)
        {
            return await this.getTransactionByHashContract(_hash, false);
        }
        const provider = this.provider;

        let transactionByHash: ethers.TransactionResponse;

        while (!transactionByHash)
        {
            await sleep(500);
            transactionByHash = await this.provider.getTransaction(_hash);
        }

        transactionByHash.wait = async function wait(
            _confirmations: number = 3,
            _timeout?: number
        ): Promise<ethers.TransactionReceipt>
        {
            let hasConfirmations = false;
            let txClone = this;

            while (!hasConfirmations)
            {
                if (
                    !txClone ||
                    typeof txClone.confirmations !== 'function' ||
                    typeof txClone.wait !== 'function' ||
                    !txClone.blockNumber
                )
                {
                    await sleep(500);
                    txClone = await provider.getTransaction(_hash);
                } else
                {
                    hasConfirmations = true;
                }
            }

            const tx = await provider.getTransaction(_hash);

            this.confirmations = await tx.confirmations();

            return await tx.wait(_confirmations, _timeout);
        };

        if (transactionByHash)
        {
            try
            {
                const confirmations = await (<any>(
                    transactionByHash
                )).confirmations();
                transactionByHash.confirmations = confirmations;
            } catch (e)
            {
                console.error(e);
                transactionByHash.confirmations = <any>0;
            }
        }

        return <any>transactionByHash;
    }

    public async getTransactionLogs(_hash: string): Promise<any>
    {
        let tx = await this.provider.getTransactionReceipt(_hash);
        console.log(tx);
        return tx;
    }

    public async getHistory(): Promise<ITransactionResponceEx[]>
    {
        if (this.isLoadNewHistory)
        {
            return this.history;
        }

        let history: ITransactionResponceEx[] = [];
        if (this.isToken)
        {
            history = await this.getHistoryContract();
        } else
        {
            history = await this.wallet.getHistory(this.chainId);

            // history = history.filter((_tx) =>
            // {
            //     if (_tx.data)
            //     {
            //         return _tx.data === '0x';
            //     }

            //     return true;
            // });
        }

        this.history = history;

        this.isLoadNewHistory = !this.isLoadNewHistory;

        return await this.mergeTxHistories();
    }

    public getTransactionDirection(
        _tx: ITransactionResponceEx
    ): TransactionDirection
    {
        const address = this.address.toLowerCase();
        const from = _tx.from.toLowerCase();
        const to = _tx.to.toLowerCase();

        if (address === to) return 'to';

        if (address === from) return 'from';

        return 'none';
    }

    public async getTransactionStatus(_tx: any): Promise<TransactionStatus>
    {
        if (_tx.state === 2)
        {
            return 'failed';
        }

        const confirmations =
            typeof _tx.confirmations === 'function'
                ? await _tx.confirmations()
                : _tx.confirmations;

        if (confirmations > 0 || _tx.state === 1)
        {
            try
            {
                await _tx.wait(1, 200);
            } catch (err)
            {
                if (err.code === 'CALL_EXCEPTION')
                {
                    return 'failed';
                }
            }
            return 'success';
        }

        return 'processing';
    }

    public getWallet(): Wallet
    {
        return this.wallet;
    }

    public async checkFeeAvailability(
        _feeData: number
    ): Promise<{ result: boolean; amount?: string; }>
    {
        const oracleFee = await this.wallet.getFeeOracle(this.chainId);
        const gasLimit = _feeData;

        const minEth = (Number(oracleFee.propose.amount) * gasLimit) / 10 ** 9;

        const balanceBN = await this.signer.provider.getBalance(
            await this.signer.getAddress()
        );

        const balance = ethers.formatEther(balanceBN);

        if (Number(balance) < minEth)
        {
            return { result: false, amount: `${ minEth } ${ this.baseSymbol }` };
        }

        return { result: true };
    }

    public async parseTransaction(
        _tx: ITransactionResponceEx,
        _parseData: boolean = true,
        _notCallContract: boolean = false
    )
    {
        return await this.parserService.parseDataVisualization(
            _tx,
            this,
            _parseData
        );
    }

    public async saveTxHistory(_txResponse: any, amount: string)
    {
        const id =
            this.id ||
            (
                await this.getWallet().getCurrencyBySymbol(
                    this.symbol,
                    _txResponse.chainId
                )
            )?.getCurrencyId() ||
            false;
        if (!id)
        {
            return;
        }
        const txHistory: IStorageHistory = {
            _currencyId: id,
            _amount: amount,
            _fee: _txResponse.gasLimit.toString(),
            _feePrice: (
                _txResponse.gasPrice ||
                _txResponse.maxFeePerGas ||
                1
            ).toString(),
            _from: _txResponse.from,
            _to: _txResponse.to,
            _timestamp: Math.floor(Date.now() / 1000),
            _state: TX_STATE.PENDING,
            _txHash: _txResponse.hash,
            _chainId: _txResponse.chainId,
            _nonce: _txResponse.nonce,
            _data: _txResponse.data || '0x',
            _value: _txResponse.value?.toString() || '0',
        };

        const custonTX = await this.txHistoryApi.setTransaction(txHistory);
        this.mergeTxHistories([ custonTX ]);
    }

    public async updateTxState(
        _hash: string,
        _txState: TX_STATE,
        _failReason: string
    )
    {
        // if (this.isLoadNewHistory) this.isLoadNewHistory = !this.isLoadNewHistory;
        await this.txHistoryApi.updateState(_hash, _txState, _failReason);
    }

    public async mergeTxHistories(_customTX = null)
    {
        const txHistory =
            _customTX ||
            (await this.txHistoryApi.getByAddressCurrency(
                this.address,
                this.id
            ));

        await txHistory.map(async (_tx) =>
        {
            _tx.gasLimit = ethers.parseUnits(_tx.fee, 'wei');
            _tx.gasPrice = ethers.parseUnits(_tx.fee_price, 'wei');
            _tx.amount = _tx.amount; //ethers.parseUnits(_tx.amount, 6);
            _tx.value = ethers.parseUnits(_tx.value || '0', 'wei');
            _tx.hash = _tx.tx_hash;
            const findTx = this.history.find(
                (elem) => elem.hash === _tx.tx_hash
            );

            if (!!findTx)
            {
                if (_tx.state === 0 && findTx.confirmations > 0)
                {
                    console.log('Check tx: ', _tx, findTx);
                    _tx.state = (await this.checkTxFail(_tx.tx_hash)).code;
                }
            } else
            {
                _tx.confirmations = 0;
                this.history.push(_tx);
            }
        });

        return this.history;
    }

    public async updateTxHash(
        _hash: string,
        _newTxhash: string,
        _feePrice: string,
        _feeLimit: string
    )
    {
        await this.txHistoryApi.updateHash(
            _hash,
            _newTxhash,
            _feePrice,
            _feeLimit
        );
    }

    public async checkTxFail(_txHash: string)
    {
        const tx: any = await this.getTransactionByHash(_txHash);

        try
        {
            await tx.wait(1);
            this.updateTxState(_txHash, TX_STATE.SUCCESS, 'inside merge');
            return { code: TX_STATE.SUCCESS, word: 'success' };
        } catch (err)
        {
            if (err.code === 'CALL_EXCEPTION')
            {
                this.updateTxState(_txHash, TX_STATE.FAILED, err.reason);
                return { code: TX_STATE.FAILED, word: 'failed' };
            }
            console.error(err);
        }
    }

    public async checkTransactionContract(
        _to: string,
        _value: string
    ): Promise<any>
    {
        const decimals = await this.contract.decimals();

        const txRequest = this.signer.populateCall({
            to: _to,
            value: ethers.parseUnits(_value, decimals),
        });

        return txRequest;
    }

    public async getBalanceContract(): Promise<any>
    {
        const balance = await this.contract.balanceOf(this.address);
        try
        {
            const decimals = await this.contract.decimals();
            const multiply = ethers.formatUnits(balance, decimals);

            return multiply;
        } catch
        {
            console.log('Incorrect decimals!');

            return ethers.formatUnits(balance, '0');
        }
    }

    public async getPriceContract(_currency = 'usd'): Promise<{
        price: string;
        price_change: string;
    }>
    {
        if (!this.contract || !(await this.contract.getAddress()))
        {
            return {
                price: '0',
                price_change: '0',
            };
        }

        return await this.pricesService.getPrice(
            this.symbol.toUpperCase(),
            _currency,
            this.chainId,
            (await this.contract.getAddress()).toLowerCase()
        );
    }

    public async getTransactionByHashContract(
        _hash: string,
        _parseData: boolean = true
    ): Promise<ITransactionResponceEx>
    {
        const tx = await this.getTransactionByHash(_hash, true, true);

        if (_parseData)
        {
            // const parsedTxData = await this.parserService.parseTxData(tx, this.abi, this.chainId, this);
            // tx.value = parsedTxData.tokens || parsedTxData.value || parsedTxData.amount || parsedTxData._value || tx.value;
            // tx.to = parsedTxData.to || parsedTxData._to || parsedTxData.recipient;
        }

        return tx;
    }

    public async getHistoryContract(): Promise<ITransactionResponceEx[]>
    {
        // TODO: add-wallets-page pages
        const explorer = await this.explorerService.getExplorer(
            this.chainId,
            this.explorerLink
        );
        let history = await explorer.GetHistory(
            this.address,
            await this.contract.getAddress(),
            { page: 1, maxPageSize: 100 }
        );

        return history;
    }

    public async populateContractTransaction(
        _transaction: TransactionLike,
        _transfer = true,
        _gasless = true
    ): Promise<any>
    {
        const feeRequired = await this.isFeeRequired();
        if (!feeRequired)
        {
            return this.populateContractTransactionOld(_transaction, _gasless);
        }

        let unsignedTx = _transaction;
        if (this.contract != null && _transfer)
        {
            unsignedTx = await this.contract.transfer.populateTransaction(
                _transaction.to,
                _transaction.value
            );
        }

        delete unsignedTx.gasPrice;
        delete unsignedTx.maxFeePerGas;
        delete unsignedTx.maxPriorityFeePerGas;

        let gas = _transaction.gasLimit;

        if (!gas)
        {
            gas = await this.signer.estimateGas(unsignedTx);
        }

        const tx = <any>(
            await this.signer.populateTransaction({
                ...unsignedTx,
                gasLimit: gas,
            })
        );

        if (!tx.value)
        {
            tx.value = ethers.parseEther('0');
        }

        return tx;
    }

    public async populateContractTransactionOld(
        _transaction: TransactionLike,
        _transfer = true,
        _gasLess = false
    ): Promise<any>
    {
        let unsignedTx = _transaction;
        if (this.contract != null && _transfer && _transaction.value)
        {
            unsignedTx = await this.contract.transfer.populateTransaction(
                _transaction.to,
                _transaction.value
            );
        }

        delete _transaction.maxPriorityFeePerGas;

        let tx;

        _gasLess
            ? (tx = unsignedTx)
            : (tx = <any>(
                await this.signer.populateTransaction({ ...unsignedTx })
            ));

        if (!tx.value)
        {
            tx.value = ethers.parseEther('0');
        }

        return tx;
    }

    public async getFeeLimitContract(
        _data: { to: string; value: string; },
        _type = 'string'
    ): Promise<string | any>
    {
        try
        {
            const decimals = await this.contract.decimals();
            const contractPopulatedTx =
                await this.contract.transfer.populateTransaction(
                    _data.to,
                    ethers.parseUnits(_data.value, decimals)
                );

            const gas = await this.signer.estimateGas(contractPopulatedTx);

            contractPopulatedTx[ 'gasLimit' ] = gas + gas / BigInt('10');

            let tx;
            try
            {
                tx = await this.signer.populateTransaction(contractPopulatedTx);
            } catch (e)
            {
                console.error(e);
                throw e;
            }

            if (_type === 'string')
            {
                return (
                    Number(ethers.formatUnits(tx.gasLimit, 'wei')) * 1.3
                ).toString();
            }
            if (_type === 'BigNumber')
            {
                return tx.gasLimit;
            }

            return tx.gasLimit;
        }
        catch (e)
        {
            console.error(e);

            if (_data.value == '0')
            {
                return 24000;
            }

            throw new Error(e);
        }
    }

    private async generateContract()
    {
        const { contract, symbol, name, abi } =
            await this.contractFactory.create(
                this.tokenAddress,
                this.chainId,
                undefined,
                { symbol: this.symbol, name: this.name }
            );

        if (!!symbol && !!name)
        {
            this.symbol = symbol;
            this.name = name;
        }
        this.contract = contract;
        this.abi = abi;
    }
}
